/**
 * 导出图片时注入紧凑表格样式的工具函数。
 * 通过向 document.head 注入临时 <style> 标签来覆盖 ali-react-table 的默认行高和间距，
 * 截图完成后移除该标签恢复页面样式。
 *
 * 进度条色块单独走 DOM inline style 覆盖（避免全局 CSS 干扰虚拟表格导致页面卡死）。
 */

const STYLE_ID = '__db_export_compact_table_style__';

/**
 * 将 svg > use 引用的外部 symbol 内联到 svg 中。
 * html2canvas 对 use 引用的 SVG 支持有限（symbol 定义可能在截图范围外），
 * 截图前把 use 替换为实际 path，截图后恢复。
 */
export const inlineSvgUseElements = (root: HTMLElement): (() => void) => {
  const restores: Array<() => void> = [];

  root.querySelectorAll('svg').forEach((svgEl) => {
    const useEl = svgEl.querySelector('use');
    if (!useEl) return;

    const href = useEl.getAttribute('xlink:href') || useEl.getAttribute('href');
    if (!href?.startsWith('#')) return;

    const symbolId = href.slice(1);
    const symbol = document.querySelector(`symbol#${symbolId}`);
    if (!symbol) return;

    const originalHTML = svgEl.innerHTML;
    const originalViewBox = svgEl.getAttribute('viewBox');

    svgEl.innerHTML = symbol.innerHTML;
    if (!svgEl.getAttribute('viewBox') && symbol.getAttribute('viewBox')) {
      svgEl.setAttribute('viewBox', symbol.getAttribute('viewBox')!);
    }

    restores.push(() => {
      svgEl.innerHTML = originalHTML;
      if (originalViewBox) {
        svgEl.setAttribute('viewBox', originalViewBox);
      } else {
        svgEl.removeAttribute('viewBox');
      }
    });
  });

  return () => {
    restores.forEach((fn) => fn());
  };
};

// 组件最外层容器 class，用于限定作用域
const SCOPE_CLASS = 'dbd-change-analysis-country-region-chart-wrap';
// 表格实例 class，提高选择器优先级以覆盖 index.scss 中的行高 / padding
const TABLE_CLASS = 'dbd-change-analysis-country-region-chart-table';

export interface IExportProgressBarOptions {
  rowHeight: number;
  progressBarHeight?: number;
}

/**
 * 计算进度条色块高度（px）。
 * 显式配置时上限为整行高度 rowHeight；未配置时按内容区高度 44% 估算。
 */
const resolveProgressBarHeight = (
  rowHeight: number,
  contentHeight: number,
  progressBarHeight?: number,
): number => {
  if (progressBarHeight !== undefined) {
    return Math.min(Math.max(1, progressBarHeight), rowHeight);
  }
  return Math.max(8, Math.floor(contentHeight * 0.44));
};

/** 色块定位容器：ali-react-table 的 td.art-table-cell */
const findProgressBarContainer = (barEl: HTMLElement): HTMLElement | null =>
  barEl.closest('td.art-table-cell') ?? barEl.closest('td');

/** 读取单元格实际渲染高度（px），读不到时回退配置行高 */
const measureContainerHeight = (container: HTMLElement | null, fallback: number): number => {
  if (!container) {
    return fallback;
  }
  const h = container.getBoundingClientRect().height;
  return Number.isFinite(h) && h > 0 ? h : fallback;
};

/**
 * 截图前直接改进度条 inline style（覆盖组件默认 height:22px / top:14px）。
 * 按每个 td 的实际渲染高度垂直居中，避免配置行高 20 但单元格仍 44 导致下方留白。
 */
export const applyExportProgressBarInlineStyles = (
  root: HTMLElement,
  options: IExportProgressBarOptions,
): (() => void) => {
  const rowHeight = Number(options.rowHeight);
  if (!Number.isFinite(rowHeight) || rowHeight <= 0) {
    return () => undefined;
  }

  const barEls = Array.from(
    root.querySelectorAll<HTMLElement>('.dbd-common-table__progress-bar'),
  ).filter(
    barEl =>
      barEl.classList.contains('dbd-common-table__progress-bar-left')
      || barEl.classList.contains('dbd-common-table__progress-bar-center'),
  );

  if (barEls.length === 0) {
    return () => undefined;
  }

  // 先统一测量、再统一写入，避免逐条 read/write 触发布局振荡
  void root.offsetHeight;

  interface IBarStylePatch {
    barEl: HTMLElement;
    barHeight: number;
    progressBarTop: number;
    prevHeight: string;
    prevHeightPriority: string;
    prevTop: string;
    prevTopPriority: string;
  }

  const patches: IBarStylePatch[] = barEls.map(barEl => {
    const container = findProgressBarContainer(barEl);
    const containerHeight = measureContainerHeight(container, rowHeight);
    const verticalPad = Math.max(2, Math.floor((rowHeight - 24) / 2));
    const contentHeight = Math.max(1, containerHeight - verticalPad * 2);
    const barHeight = resolveProgressBarHeight(
      containerHeight,
      contentHeight,
      options.progressBarHeight,
    );
    const progressBarTop = Math.max(0, Math.floor((containerHeight - barHeight) / 2));

    return {
      barEl,
      barHeight,
      progressBarTop,
      prevHeight: barEl.style.getPropertyValue('height'),
      prevHeightPriority: barEl.style.getPropertyPriority('height'),
      prevTop: barEl.style.getPropertyValue('top'),
      prevTopPriority: barEl.style.getPropertyPriority('top'),
    };
  });

  const restores: Array<() => void> = [];
  patches.forEach(patch => {
    patch.barEl.style.setProperty('height', `${patch.barHeight}px`, 'important');
    patch.barEl.style.setProperty('top', `${patch.progressBarTop}px`, 'important');
    restores.push(() => {
      if (patch.prevHeight) {
        patch.barEl.style.setProperty(
          'height',
          patch.prevHeight,
          patch.prevHeightPriority,
        );
      } else {
        patch.barEl.style.removeProperty('height');
      }
      if (patch.prevTop) {
        patch.barEl.style.setProperty('top', patch.prevTop, patch.prevTopPriority);
      } else {
        patch.barEl.style.removeProperty('top');
      }
    });
  });

  return () => {
    restores.forEach(fn => fn());
  };
};

/**
 * 根据 rowHeight、headerFontSize 和 contentFontSize 生成导出图片紧凑表格的 CSS 文本
 */
const buildCompactCss = (
  rowHeight: number,
  headerFontSize = 15,
  contentFontSize = 15,
): string => {
  const rowHeightPx = `${rowHeight}px`;
  const verticalPad = Math.max(2, Math.floor((rowHeight - 24) / 2));
  const cellPadding = `${verticalPad}px 20px`;
  const headerFontSizePx = `${headerFontSize}px`;
  const contentFontSizePx = `${contentFontSize}px`;
  const tableScope = `.${SCOPE_CLASS} .${TABLE_CLASS}`;

  return `
    ${tableScope} .db-alitable-wrapper {
      --row-height: ${rowHeightPx} !important;
      --header-row-height: ${rowHeightPx} !important;
    }
    ${tableScope} .dbd-common-table__content table thead {
      height: ${rowHeightPx} !important;
    }
    ${tableScope} .dbd-common-table__content table thead tr {
      height: ${rowHeightPx} !important;
    }
    ${tableScope} .dbd-common-table__content table tbody tr {
      height: ${rowHeightPx} !important;
    }
    ${tableScope} .dbd-common-table__content table thead th,
    ${tableScope} .dbd-common-table__content table thead th[class][class][class] {
      padding: ${cellPadding} !important;
      height: ${rowHeightPx} !important;
      font-size: ${headerFontSizePx} !important;
    }
    ${tableScope} .art-table-header-cell {
      padding: ${cellPadding} !important;
      font-size: ${headerFontSizePx} !important;
    }
    ${tableScope} .dbd-common-table__header-sort-clickable {
      margin: -${verticalPad}px -20px !important;
      padding: ${cellPadding} !important;
      font-size: ${headerFontSizePx} !important;
    }
    ${tableScope} .dbd-common-table__content table tbody td {
      padding: ${cellPadding} !important;
      height: ${rowHeightPx} !important;
      font-size: ${contentFontSizePx} !important;
      box-sizing: border-box !important;
    }
    ${tableScope} .db-metrics__num {
      font-size: ${contentFontSizePx} !important;
    }
    ${tableScope} .db-metrics--status-down,
    ${tableScope} .db-metrics--status-up {
      font-size: ${contentFontSizePx} !important;
    }
    ${tableScope} .dbd-common-table__content table thead th,
    ${tableScope} .dbd-common-table__content table thead th[class][class][class],
    ${tableScope} .art-table-header-cell {
      border-top-color: #3B3D4B !important;
      border-bottom: 1px solid #3B3D4B !important;
      border-right-color: #3B3D4B !important;
      border-left-color: #3B3D4B !important;
    }
    ${tableScope} .dbd-common-table__content table tbody td {
      border-top-color: #3B3D4B !important;
      border-bottom-color: #3B3D4B !important;
      border-right-color: #3B3D4B !important;
      border-left-color: #3B3D4B !important;
    }
  `;
};

/**
 * 注入紧凑表格导出样式
 * @param rowHeight 行高（px）
 * @param headerFontSize 表头字体大小（px），默认 15
 * @param contentFontSize 表内容字体大小（px），默认 15
 */
export const injectCompactTableStyle = (
  rowHeight: number,
  headerFontSize = 15,
  contentFontSize = 15,
): void => {
  const existing = document.getElementById(STYLE_ID);
  if (existing) {
    existing.remove();
  }
  const styleEl = document.createElement('style');
  styleEl.id = STYLE_ID;
  styleEl.textContent = buildCompactCss(rowHeight, headerFontSize, contentFontSize);
  document.head.appendChild(styleEl);
};

/**
 * 移除紧凑表格导出样式
 */
export const removeCompactTableStyle = (): void => {
  const injected = document.getElementById(STYLE_ID);
  if (injected) {
    injected.remove();
  }
};
